import type { Messages } from './fr';

const en: Messages = {
  app: { tagline: 'The word game that speaks several languages.' },
  nav: {
    back: 'Back',
    home: 'Home',
    rules: 'Rules',
    settings: 'Settings',
    about: 'About',
    account: 'Account',
    leaderboards: 'Leaderboards',
    tournaments: 'Tournaments',
    daily: 'Daily challenge',
    admin: 'Administration',
  },
  common: {
    cancel: 'Cancel',
    confirm: 'Confirm',
    close: 'Close',
    save: 'Save',
    loading: 'Loading…',
    yes: 'Yes',
    no: 'No',
    copy: 'Copy',
    copied: 'Copied!',
    retry: 'Retry',
  },
  langNames: {
    fr: 'French',
    en: 'English',
    es: 'Spanish',
    de: 'German',
    it: 'Italian',
    pt: 'Portuguese',
  },
  home: {
    quickPlay: 'Play',
    quickPlayDesc: '2-minute Classic game · {{lang}}',
    modesTitle: 'Game modes',
    newGame: 'New game',
    online: 'Play online',
  },
  modes: {
    classic: {
      name: 'Classic',
      short: 'One language · 2 min',
      desc: 'One language, the same for everyone. Score as many points as you can in 120 seconds. No language bonus and no early-finish bonus.',
    },
    polyglot: {
      name: 'Shared Polyglot',
      short: '2 or 3 languages for all · 2 min',
      desc: 'The same 2 or 3 languages for everyone. Diversity bonus (+20 % with two languages, +30 % with three) if you find at least 3 words in each language.',
    },
    duel: {
      name: 'Tactical Duel',
      short: '10 words or 2 min · contract',
      desc: 'Each player secretly picks 1, 2 or 3 languages. Goal: 10 valid words. Language contract + speed bonus if you reach 10 words before time runs out. Your 10th word ends your round.',
    },
    sprint: {
      name: 'Free Sprint',
      short: '1 to 3 languages · 2 min · contract',
      desc: 'Pick 1 to 3 languages yourself and score as much as possible in 120 seconds with a language contract. No bonus for finishing early.',
    },
    relax: {
      name: 'Relax',
      short: 'No timer',
      desc: 'No time limit, no ranking. Validity hints can be enabled in this mode only.',
    },
  },
  setup: {
    title: 'New game',
    mode: 'Mode',
    languagesShared: 'Game languages ({{min}} to {{max}})',
    languagesIndividual: 'Your languages (1 to 3, locked before the draw)',
    errors: {
      duplicate_language: 'Each language can only be chosen once.',
      too_few_languages: 'Choose at least {{min}} language(s).',
      too_many_languages: 'At most {{max}} language(s).',
    },
    noLanguageBonus: 'No language bonus in this mode.',
    opponent: 'Opponent',
    noOpponent: 'None (solo)',
    robotNotice:
      'Robots are training programs, clearly labelled. They never count in leaderboards.',
    robotSameLanguages: 'The robot plays with my languages',
    robotLanguages: 'Robot languages',
    loading: 'Preparing the draw…',
    start: 'Start',
    noDraw:
      'No balanced draw was found for this combination. Try other languages or the Specialist contract.',
  },
  contracts: {
    specialist: { name: 'Specialist', desc: 'one language, bonus granted on your base points.' },
    duo: { name: 'Duo', desc: 'two languages, bonus only with at least 3 distinct words in each.' },
    trio: {
      name: 'Trio',
      desc: 'three languages, bonus only with at least 3 distinct words in each.',
    },
    diversity: {
      name: 'Diversity',
      desc: '{{pct}} % bonus for everyone with at least 3 words per language.',
    },
    short: { specialist: 'Specialist', duo: 'Duo', trio: 'Trio', diversity: 'Diversity' },
  },
  bots: {
    rookie: 'Rookie',
    casual: 'Casual',
    skilled: 'Skilled',
    expert: 'Expert',
    rookieDesc: 'Very common words, slow pace, prefers short words.',
    casualDesc: 'Common words, moderate pace.',
    skilledDesc: 'Broad common vocabulary, steady pace.',
    expertDesc: 'Also knows rare words, fast, goes for long words.',
    robotName: '{{level}} robot',
  },
  game: {
    timeLeft: 'Time left',
    getReady: 'Ready…',
    untimed: '∞',
    score: 'Score',
    points: 'pts',
    wordsOfTarget: '{{n}} / {{target}} words',
    wordsCount_one: '{{count}} word',
    wordsCount_other: '{{count}} words',
    languageCounters: 'Words per language',
    robotNotice: 'Robot opponent (training program)',
    opponentDone: 'finished',
    finalWarning:
      'Careful: your next valid word will be the 10th and will end the round. Your contract is not fulfilled yet.',
    currentWord: 'Current word',
    tapLetters: 'Tap the letters',
    hintValid: 'Valid: {{langs}}',
    hintInvalid: 'Not a word (yet)',
    attributeTo: 'Assign to {{lang}}',
    attribution: 'Language:',
    auto: 'Auto',
    autoExplain:
      'Auto: the word goes to the valid language that most needs words for your contract, otherwise in your language order.',
    rack: 'Draw letters',
    tileLabel: 'Letter {{letter}}, position {{pos}}',
    shuffle: 'Shuffle',
    delete: 'Delete',
    clear: 'Clear',
    submit: 'Submit',
    foundWords: 'Found words',
    bonusSecured: 'language bonus secured',
    finish: 'Finish',
    stop: 'Stop',
    stopTitle: 'Stop the round?',
    stopBody: 'Your words are kept. Stopping early gives no bonus.',
    finishBody: 'Finish this relax game and see the summary?',
    confirmFinalTitle: 'Last word: contract not fulfilled',
    confirmFinalOk: 'Submit anyway',
    confirmFinalBody:
      'This 10th word ends your round immediately. Your language contract will not be fulfilled: no language bonus (the speed bonus still applies).',
    accepted: '“{{word}}” +{{points}} ({{lang}})',
  },
  reasons: {
    round_over: 'The round is over.',
    invalid_characters: 'Characters cannot be played.',
    too_short: 'Three letters minimum.',
    too_long: 'Word too long.',
    not_formable: 'Not possible with these letters.',
    duplicate: 'Already found.',
    language_not_allowed: 'Language not allowed in this round.',
    excluded_word: 'Word excluded by the game’s content policy.',
    not_a_word: 'Unknown word in your languages.',
    wrong_language: 'This word is valid in: {{langs}}.',
    confirmation_required: 'Confirmation required.',
    rate_limited: 'Too many attempts, slow down.',
    not_started: 'The round has not started yet.',
  },
  results: {
    points: 'points',
    pts: 'pts',
    base: 'Base points',
    fulfilled: 'fulfilled',
    missed: 'missed',
    speedBonus: 'Speed bonus',
    officialTime: 'Official time',
    target: 'Goal',
    endReason: {
      target_reached: 'Goal reached',
      time_up: 'Time up',
      stopped: 'Stopped',
      abandoned: 'Abandoned',
    },
    explainBase: 'Your words are worth {{base}} base points (10 × (letters − 2)).',
    explainSpecialist: 'Specialist contract: +10 % granted.',
    explainQuotaOk: 'At least {{quota}} words in each language: {{pct}} % bonus granted.',
    explainQuotaKo:
      'You needed at least {{quota}} words in each language: no language bonus ({{pct}} % possible).',
    explainSpeedOk: '10 words reached in {{time}}: speed bonus of {{pct}} %.',
    explainSpeedKo: '10-word goal not reached in time: no speed bonus.',
    explainFormula:
      'Total = base × (1 + language bonus + speed bonus), rounded once to the hundredth.',
    win: 'Victory!',
    lose: 'Defeat',
    tie: 'Tie',
    tieExplain: 'Exactly identical scores: the match is declared a tie.',
    you: 'You',
    robotTitle: '{{name}} (robot)',
    missedWords: 'Common words you could have formed',
    missedWordsNote: 'An indicative list of frequent words in your languages, for next time.',
    shareText: '{{mode}} ({{langs}}): {{score}} points, {{words}} words. Can you beat it?',
    playAgain: 'Play again',
    share: 'Share result',
    changeSetup: 'Change mode',
    localNotice: 'Local game: never enters any leaderboard.',
    savedToHistory: 'Saved in “My games” (practice).',
    drawAudit: 'Draw {{seed}} ({{gen}})',
  },
  errors: {
    network: 'Connection lost. Retrying…',
    lexiconLoad: 'Could not load the dictionary. Check your connection and try again.',
    notFound: 'Page not found',
    generic: 'Something went wrong.',
  },
  online: {
    title: 'Online',
    challengeFriend: 'Challenge a friend',
    challengeFriendDesc: 'Invitation link, each plays when they like',
    liveDuel: 'Live duel',
    liveDuelDesc: 'Room, invitation, simultaneous game',
    myGames: 'My games',
    offlineNote: 'Server unreachable: only solo and training are available.',
    guestBanner:
      'You are playing as a guest. Create an account to keep your history and join leaderboards.',
    saveAccount: 'Save my account',
  },
  challenge: {
    newTitle: 'Challenge a friend',
    returnMatch: 'Return match (2 legs, swapped draws, unranked)',
    create: 'Create challenge',
    created: 'Challenge created! Send this link:',
    share: 'Share link',
    shareText: 'I challenge you ({{mode}})!',
    expires: 'Expires on {{date}}',
    playMine: 'Play my round now',
    inviteFrom: '{{name}} challenges you!',
    acceptTitle: 'Invitation',
    chooseLanguages: 'Choose your languages (locked once confirmed)',
    compatibleOnly: 'Only combinations compatible with this challenge’s draw are offered.',
    accept: 'Accept challenge',
    expired: 'This challenge has expired.',
    taken: 'This challenge was already accepted by someone else.',
    own: 'This is your own challenge: share the link with a friend.',
    notFound: 'Challenge not found.',
    returnInfo:
      'Return match: two legs on two different draws; no result is revealed before the end.',
    goToMatch: 'Go to the game',
  },
  match: {
    readyTitle: 'Your round',
    readyBody:
      'The timer starts as soon as the letters appear. Closing or reloading the page does not reset the time.',
    start: 'Start my round',
    waitingOpponent: 'Waiting for your opponent. Their words and score stay hidden until the end.',
    opponentProgress: '{{name}}: {{count}} word(s)',
    leg: 'Leg {{n}} / 2',
    nextLeg: 'Play the next leg',
    encounterTotal: 'Match total',
    unranked: 'Unranked game',
    ranked: 'Ranked game',
    reportWord: 'Report a refused word',
    reportPrompt: 'Word to report (and language)',
    reportSent: 'Thanks, report sent.',
    loadError: 'Could not load the game.',
    opponentNotPlayed: 'The opponent did not play.',
    opponent: 'Opponent',
  },
  account: {
    title: 'My account',
    guest: 'Guest account',
    registered: 'Saved account',
    register: 'Create an account',
    login: 'Log in',
    logout: 'Log out',
    username: 'Username (3–20 characters)',
    password: 'Password (min. 10 characters)',
    email: 'Email (optional)',
    displayName: 'Display name',
    saveHint: 'Your guest history is kept when you create your account.',
    history: 'History',
    export: 'Export my data',
    delete: 'Delete my account',
    deleteConfirm: 'Permanently delete your account? Your personal data will be erased.',
    notifications: 'Reminders',
    remindersOptIn: 'Receive reminders (at most one a day, never during quiet hours)',
    quietHours: 'Quiet hours (from … to …)',
    haveAccount: 'Already have an account?',
    noAccount: 'No account yet?',
    saved: 'Saved.',
    errors: {
      invalid_credentials: 'Wrong username or password.',
      username_or_email_taken: 'Username or email already in use.',
      nickname_refused: 'This name is not allowed.',
      nickname_reserved: 'This name is reserved.',
      nickname_length: 'Between 3 and 20 characters.',
      nickname_characters: 'Characters not allowed.',
      invalid_request: 'Please check the fields.',
      account_suspended: 'Account suspended.',
      generic: 'Something went wrong.',
    },
  },
  live: {
    searching: 'Looking for an opponent…',
    searchingBody: 'Waiting: {{seconds}} s',
    noFakePlayers: 'Only real players are matched here; no robot is ever presented as a human.',
    fallbackTitle: 'Nobody yet. You can:',
    fallbackRobot: 'Play against a robot (training, unranked)',
    fallbackAsync: 'Create an asynchronous challenge to share',
    othersSearching_zero: 'No other player is looking for this kind of game right now.',
    othersSearching_one: '{{count}} other player is looking for this kind of game right now.',
    othersSearching_other: '{{count}} other players are looking for this kind of game right now.',
    noOpponentFound: 'Nobody is available right now. You can:',
    searchAgain: 'Search again',
    languagesSecret: 'Your languages (secret, locked before the draw)',
    quick: 'Quick match',
    createRoom: 'Create a room and invite',
    rankedNote:
      'Games between saved accounts count for the leaderboard (official or experimental depending on the mode).',
    roomFull: 'This room is full.',
    roomNotFound: 'Room not found or expired.',
    offline: 'disconnected',
    online: 'connected',
    opponentReady: 'ready',
    waitingOpponent: 'Waiting for an opponent…',
    shareRoom: 'Invite a friend with this link:',
    sharedReady: 'Languages are set for both players.',
    lock: 'I’m ready',
    waitingLock: 'Waiting for your opponent’s choice…',
    noDrawForBoth: 'No balanced draw for both configurations. Choose other languages.',
    disconnectRule:
      'Disconnection rule: the server clock keeps running; come back to this page to resume your round where it stands.',
    opponentLeft:
      '{{name}} disconnected. Their clock keeps running; they can come back before their round ends.',
    opponentBack: '{{name}} is back.',
    reconnecting: 'Connection lost, reconnecting… Words already accepted are kept.',
    rematch: 'Rematch',
    joinRematch: 'Join the rematch',
    reactionsTitle: 'Reactions',
    reactions: {
      gg: 'Good game!',
      bravo: 'Well done!',
      wow: 'Impressive!',
      oops: 'Oops!',
      thanks: 'Thanks!',
      good_luck: 'Good luck!',
    },
  },
  boards: {
    title: 'Leaderboards',
    official: 'Official',
    experimental: 'Experimental',
    officialDesc: 'Symmetric modes (Classic, Shared Polyglot): same languages for everyone.',
    experimentalDesc:
      'Tactical Duel and Free Sprint: separate board, not comparable with symmetric modes.',
    myRank: 'Your rank: {{rank}} · {{rating}} Elo · {{games}} games',
    empty: 'No results yet.',
    player: 'Player',
    rating: 'Elo',
    games: 'Games',
    rules:
      'Only games between saved accounts count. Robots, local games and return matches never enter leaderboards.',
  },
  tournaments: {
    title: 'Tournaments',
    intro:
      'Weekly asynchronous tournaments: several rounds, one attempt per round, same letters for everyone.',
    none: 'No tournament at the moment.',
    weeklyTitle: 'Weekly {{mode}} {{langs}} · {{period}}',
    rounds: '{{n}} round(s)',
    entrants: '{{n}} entrant(s)',
    registered: 'registered',
    status: {
      scheduled: 'Upcoming',
      open: 'Running',
      closing: 'Closing',
      closed: 'Finished',
      cancelled: 'Cancelled',
    },
    frozen: 'Rules {{rules}} and dictionaries {{lexicons}} are frozen for the whole tournament.',
    attempts: '{{n}} attempt(s) per round.',
    sharingLimit:
      'Known limit: in a public asynchronous competition, players can share answers. The game does not claim to fully prevent it.',
    register: 'Register',
    accountRequired: 'A saved account is required to play tournaments.',
    myRounds: 'My rounds',
    round: 'Round {{n}}',
    resume: 'Resume',
    play: 'Play',
    standings: 'Tournament standings',
    roundsPlayed: '{{n}} round(s)',
    tieRule: 'Exact tie: same rank, no hidden tiebreak.',
    backToTournament: 'Back to the tournament',
    finalStandings: 'Final standings',
    myResult: 'Your total: {{total}} points · rank {{rank}} of {{n}}',
    noRoundYet: 'No round played yet.',
    startsAt: 'Starts: {{date}}',
    abandoned: 'Not finished',
    retry: 'New attempt ({{n}} left)',
    closingNote:
      'Closing: rounds already started may finish; final standings follow within a few minutes.',
    errors: {
      competition_not_found: 'Tournament not found.',
      invitation_required: 'This tournament is private: an invitation is required.',
      attempts_exhausted: 'Attempt already used for this round.',
      competition_closed: 'The tournament is over.',
      competition_not_started: 'The tournament has not started.',
      not_registered: 'Register first.',
      account_required: 'Saved account required.',
    },
  },
  daily: {
    title: 'Daily challenge',
    intro: 'One draw per category and per day (UTC), the same for everyone. One attempt only.',
    back: 'Back to daily challenges',
    play: 'Play',
    inProgress: 'In progress',
    resets: 'New challenge on {{time}}.',
  },
  profile: {
    title: 'My profile',
    streak: 'day streak',
    bestStreak: 'best streak',
    rounds: 'online games',
    distinctWords: 'distinct words',
    streakRules:
      'The streak gains a day when you finish an online game with at least one valid word (daily challenge, challenge with friends, live duel or tournament), at most once a day. Free grace days left: {{grace}} (renewed every 30 days). Time zone: {{tz}}.',
    useMyTimezone: 'Use my time zone ({{tz}})',
    tzTooSoon: 'The time zone can only change once every 7 days.',
    badges: 'Badges',
    noBadges: 'No badge yet.',
    badgesNote: 'Badges only rely on events verified by the server.',
    cosmetics: 'Customisation',
    cosmeticsNote: 'Purely visual: no effect on score or draws.',
    plusOnly: 'Plus only',
    earnIt: 'Unlock by playing',
    slot: {
      theme: 'Theme',
      avatar: 'Avatar',
      frame: 'Frame',
    },
    history: 'My games',
    gamesSummary: '{{online}} online · {{practice}} practice',
    noGames: 'No saved game yet.',
    pendingSync: '{{n}} practice round(s) waiting to be sent (offline).',
    gamesNote:
      'Practice rounds (solo, robots, relax) are played on your device, then saved here. The server checks the words and recomputes the score but cannot verify timing: they never count for the streak, badges or leaderboards.',
    sourceOnline: 'Online',
    sourcePractice: 'Practice',
    waitingOpponent: 'result once the opponent plays',
    playDaily: 'Play the daily challenge',
  },
  badges: {
    first_round: 'First round',
    nine_letters: '9-letter word',
    trio_contract: 'Trio contract fulfilled',
    speed_60: '10 words in under 60 s',
    words_100: '100 words found',
    words_1000: '1,000 words found',
    streak_7: '7-day streak',
    streak_30: '30-day streak',
    first_duel_win: 'First duel win',
    tournament_participant: 'Tournament participant',
    tournament_winner: 'Tournament winner',
    tournament_podium: 'Tournament podium',
    tournament_top10: 'Tournament top 10',
    daily_participant: 'Daily challenge',
    team_weekly: 'Team goal reached',
  },
  cosmetics: {
    theme_default: 'Classic',
    theme_ocean: 'Ocean',
    theme_aurora: 'Aurora',
    theme_sakura: 'Sakura',
    theme_mono: 'Monochrome',
    avatar_letter: 'Initial',
    avatar_owl: 'Owl',
    avatar_fox: 'Fox',
    frame_none: 'None',
    frame_team: 'Team',
    frame_champion: 'Champion',
  },
  reminders: {
    title: 'Reminders',
    explain:
      'Optional: at most one reminder a day, never during your quiet hours, and none if you already played. Turn off anytime.',
    unavailable: 'Reminders are not available on this server or device.',
    quietStart: 'Quiet hours: start',
    quietEnd: 'Quiet hours: end',
    enable: 'Turn on reminders',
    disable: 'Turn off',
    enabled: 'Reminders on.',
    disabled: 'Reminders off.',
    denied: 'Permission denied by the browser.',
  },
  journal: {
    title: 'Word journal',
    intro: 'Words you found online, by language.',
    all: 'All',
    search: 'Search a word',
    empty: 'No words yet.',
  },
  friends: {
    title: 'Friends',
    inbox: 'Challenges received',
    inboxNote: 'Open the link your friend sent you to play.',
    invite: 'Invite a friend',
    inviteNote: 'Link valid for 7 days. No free chat: only challenges and predefined reactions.',
    none: 'No friends yet.',
    challenge: 'Challenge',
    remove: 'Remove',
    ownInvite: 'This is your own invitation.',
    inviteInvalid: 'Invalid or expired invitation.',
  },
  team: {
    title: 'Team',
    intro:
      'Join or create a team (max 20 members) and reach a weekly word goal together. Cosmetic rewards only.',
    name: 'Team name',
    create: 'Create team',
    weekly: 'Goal for week {{week}}',
    progress: '{{words}} / {{goal}} words',
    rewardNote: 'Goal reached: “Team” frame for every member.',
    members: 'Members ({{n}})',
    leave: 'Leave team',
    accountRequired: 'A saved account is required.',
    joinFailed: 'Could not join this team.',
  },
  moderation: {
    block: 'Block this player',
    blocked: 'Player blocked.',
    report: 'Report…',
    reported: 'Report sent.',
    reasons: {
      nickname: 'Inappropriate name',
      cheating: 'Suspected cheating',
      harassment: 'Harassment',
      other: 'Other',
    },
  },
  plus: {
    intro: 'Plus supports the game and adds tools, never touching the score:',
    fairness:
      'The core game, six languages, training, challenges and public competition stay free. No difficulty setting depends on the subscription.',
    features: {
      advanced_stats: 'Advanced statistics',
      challenge_archives: 'Full archive of finished challenges',
      personal_training: 'Deterministic personalised training',
      customisation: 'Exclusive themes and avatars',
      private_tournaments: 'Private tournament tools',
    },
    monthly: 'Subscribe — {{price}}',
    yearly: 'Yearly plan — {{price}}',
    unavailable: 'Plus is not available for purchase yet.',
    testMode: 'Test mode: no real payment.',
    confirming: 'Payment being confirmed… (granted only once the provider confirms)',
    cancelled: 'Payment cancelled.',
    active: 'Plus active',
    until: 'until {{date}}',
    manage: 'Manage my subscription',
    stats: 'Statistics',
    mode: 'Mode',
    rounds: 'Rounds',
    avgScore: 'Average score',
    contractRate: 'Contracts met',
    secPerWord: 's / word',
    train: 'Personalised training ({{lang}})',
    archive: 'Finished challenge archive',
    archiveEmpty: 'No finished game yet.',
    archiveMore: 'Show more',
    archiveBot: 'robot',
    pricesNote: 'Indicative prices that may change; the exact amount is shown on the payment page.',
    notOffered: 'No subscription is offered in this version: the whole game is free.',
  },
  legal: {
    draftBanner: 'Draft — to be completed with the publisher’s real identity.',
    privacy: 'Privacy',
    terms: 'Terms',
    notice: 'Legal notice',
  },
  offline: {
    title: 'Play offline',
    explain:
      'Download dictionaries to play solo and train without a connection. Offline games never enter any leaderboard.',
    download: 'Download',
    ready: 'Available',
  },
  play: { noGame: 'No game in progress.' },
  install: {
    body: 'Install the game on your home screen to play faster, even offline in solo.',
    cta: 'Install',
    later: 'Later',
  },
  pwa: {
    updateReady: 'A new version is ready.',
    reload: 'Update',
    offline:
      'Offline: solo and training remain available with the dictionaries already downloaded.',
  },
  settings: {
    title: 'Settings',
    uiLanguage: 'Interface language',
    uiLanguageNote:
      'The interface language is independent from the languages allowed in your games.',
    sound: 'Sounds',
    haptics: 'Vibration (if supported)',
    motion: 'Animations',
    motionSystem: 'System setting',
    motionReduced: 'Reduced',
    motionFull: 'Normal',
    relaxHints: 'Validity hints in Relax mode',
  },
  rules: {
    title: 'Game rules',
    goal: {
      title: 'Goal',
      body: [
        'Make words with the 9 letters of the draw by tapping them in any order — no adjacency needed.',
        'Each tile can be used once per word; all tiles are available again for the next word. Shuffling does not change the draw.',
        'Three letters minimum. A word only scores once per round.',
      ],
    },
    tiles: {
      title: 'Letters and accents',
      body: [
        'Accents and cedillas have no tile of their own: French “élève” is played E-L-E-V-E and shown with its accents, so “élève” and “élevé” are the same playable word.',
        'Ñ (Spanish) and Ä, Ö, Ü (German) are distinct tiles, usable only in their language. ß is spelled with two S; œ and æ use two tiles.',
      ],
    },
    languages: {
      title: 'Languages and assignment',
      body: [
        'A word valid in several of your languages is assigned to only one. Pick the language before submitting (Language buttons) or keep “Auto”.',
        'Auto picks the valid language that most needs words for your contract, then your language order. Once accepted, a word never changes language.',
        'Proper nouns and abbreviations are excluded. Some offensive words are excluded by the content policy. You will be able to report a refused word.',
      ],
    },
    points: {
      title: 'Points',
      body: [
        'A word of n letters (tiles) scores 10 × (n − 2): 3 letters = 10, 4 = 20 … 9 = 70.',
        'Final score = base points × (1 + language bonus + speed bonus). Bonuses add up and the total is rounded once to the hundredth.',
        'A refused word scores nothing and does not count toward the goal.',
      ],
    },
    contracts: {
      title: 'Contracts (Tactical Duel, Free Sprint)',
      body: [
        'Specialist (1 language): +10 %. Duo (2 languages): +20 % with at least 3 words in each. Trio (3 languages): +30 % with at least 3 words in each.',
        'Missed contract: you keep your base points, with no language bonus and no fallback to a lower contract.',
        'Shared Polyglot: +20 % (2 languages) or +30 % (3 languages) with at least 3 words per language, identical for everyone.',
      ],
    },
    speed: {
      title: 'Speed bonus (Tactical Duel)',
      body: [
        'Reach 10 valid words before 120 s: bonus = 20 % × (120 − t) / 120, where t is the official time measured by the server. At 90 s: +5 %; at 60 s: +10 %; at 30 s: +15 %.',
        'The 10th accepted word ends your round immediately. The game warns you if your contract is not fulfilled.',
      ],
    },
    modes: {
      title: 'Modes',
      body: [
        'Classic, Shared Polyglot, Tactical Duel, Free Sprint, Relax (no timer, no ranking), and training against robots.',
        'Exact tie: the match is declared a tie, with no hidden speed tiebreak.',
      ],
    },
    fairness: {
      title: 'Fairness',
      body: [
        'The same draw is not equally hard in every language. Each draw is checked to offer enough common words to each configuration, without claiming perfect fairness.',
        'Parameters are hypotheses under test; a game always keeps the rules and dictionary versions it started with.',
      ],
    },
    robots: {
      title: 'Robots',
      body: [
        'Robots are deterministic programs (no generative AI), labelled as such. They only play valid words and never enter human leaderboards.',
      ],
    },
    keyboard: {
      title: 'Keyboard',
      body: 'Type letters (or 1–9 for a tile position), Enter to submit, Backspace to delete, Escape to clear, Space to shuffle.',
    },
  },
  about: {
    title: 'About',
    disclaimer:
      'A word game for the fun of playing in several languages. It has no demonstrated educational effectiveness.',
    dictionaries: 'Dictionaries and data',
    frequencies: 'Word frequencies',
    policy: 'Content policy',
    adapted: 'adapted list',
    noticeFiles: 'Full notices (sources, versions, licenses) come with each downloaded dictionary.',
    noDefinitions:
      'No definitions or translations are shown until a compatibly licensed source is integrated.',
  },
};

export default en;
